import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { ContactRow } from '@shared/flow/contact-view';
import { joinNonBlank } from '@shared/flow/non-blank';
import { FlowDetail, DetailKV, type FlowDetailPanel } from '../FlowDetail';
import { ContactTimeline } from './ContactTimeline';
import { ContactIntelPanel } from './ContactIntelPanel';
import { PromptSettingsGear } from '../PromptSettingsGear';
import { ContactActionMenus } from './ContactActionMenus';
import { SfdcCloudIcon, LinkedInIcon, sfdcRecordUrl } from '../links';
import { accountInCommandCenter, contactsInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import { AccountIntelPanel } from '../account/AccountIntelPanel';
import { AccountFieldsCard } from '../account/AccountDetailPanels';
import { useFlowAccount } from '../account/hooks';
import { useContactStateDetail } from './hooks';
import { StateFactCard, ViewAllRequestsButton } from './StatePills';
import { RoleChips, coerceUsageRoles } from '../../shared/RoleChips';
import type { Surface } from '@shared/analytics/events';

/**
 * RENDER limit for the Recent Actions section (flow-workflow-requests,
 * "timestamps + View all") — kept in sync BY HAND with the backend's
 * `RECENT_REQUEST_MAX_PER_CONTACT` (src/services/flow/contact-state-sources.ts),
 * same disclaimer as StatePills.tsx's SUGGESTED_INVITE_LABEL etc.: core
 * services and this frontend bundle don't share runtime code across that
 * boundary. The backend now resolves up to `RECENT_REQUEST_FETCH_LIMIT` (3)
 * facts specifically so this component can detect "a 3rd exists" from the
 * array length alone, with no separate count endpoint.
 */
const RECENT_REQUEST_RENDER_LIMIT = 2;

/**
 * The contact detail panels — the Terra Actions strip (full-width, above the
 * grid — see TerraNotesStrip below), then the panel grid in order:
 * Contact | Contact Timeline | Intelligence | Account, fed into the generic
 * FlowDetail container. The Account Intelligence / Account Info panels
 * always render (D10, `plans/2026-09-22-contact-actions-intelligence-
 * implementation-plan.md`) — `hideAccountPanel` is retired: a rep opening a
 * contact from inside an account context still benefits from seeing that
 * account's intel/info alongside it (e.g. confirming they're on the right
 * account), and the D7 <1600px tab strip means the "always 4-5 panels" cost
 * is a tab away, not a wider grid.
 *
 * The notes footer row (Contact Notes | Account Notes) was removed (Bucket B
 * U4, R3 — Notes UI paused pending Terra's mirror-vs-live-callback
 * consistency decision); `FlowDetail`'s generic `footer` prop is unused here
 * now but kept for future use.
 */

function fmt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Rich Account panel (global mount only): the full account, fetched by id.
 * Loading → muted note; `'resolving'` → the inline force-fetch note (Trigger
 * 3, below); loaded → name + SFDC link + the sectioned fields card;
 * null/404 → the thin fallback built from the contact row.
 */
function AccountPanel({ row }: { row: ContactRow }) {
  const { data, isLoading } = useFlowAccount(row.account_id);
  const account = data?.account;

  if (row.account_id && isLoading) {
    return <div className="text-[12px] text-content-tertiary dark:text-nightshade-500">Loading…</div>;
  }

  // Trigger 3 (plan U4 / R6/R7): this contact's `account_id` had no mirror
  // row, so GET /api/flow/accounts/:id fired the on-demand Salesforce resolve
  // and answered `state: 'resolving'`. No extra wiring is needed here — the
  // shared `useFlowAccount` hook already polls (bounded, plan D11) and this
  // panel flips to the rich card above the moment the row lands. On give-up
  // the hook yields `null` and the thin fallback below takes over.
  //
  // Contact-panel variant of the account page's copy: the resolve mirrors the
  // ACCOUNT record only (Middleman has no contact-record resolve — plan Q6),
  // so the fields arrive on the next hourly bulk pass.
  if (data?.state === 'resolving') {
    return (
      <div data-testid="account-panel-resolving">
        <div className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100 mb-1.5 min-w-0 truncate">
          {row.account_name ?? '—'}
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-content-tertiary dark:text-nightshade-500">
          <Loader2 className="w-3 h-3 animate-spin shrink-0" aria-hidden="true" />
          Fetching from Salesforce…
        </div>
        <p className="mt-1 text-[11.5px] text-content-tertiary dark:text-nightshade-500 leading-relaxed">
          This account isn't in Terra's mirror yet — details sync within the hour.
        </p>
      </div>
    );
  }

  if (account) {
    // Viewable split (plan D3/D4, U2): 'not_synced' (old-backend skew) renders
    // the same rich fields card as 'synced' — any mirrored, non-deleted
    // account is fully viewable now. `suppressed` stays a plain field inside
    // AccountFieldsCard, unchanged by this plan (D6 cut — no banner here).
    return (
      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1.5">
          <div className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100 min-w-0 truncate">{account.name ?? '—'}</div>
          {/* Terra ↗ (2026-08-28, Spencer) — the account's own Command Center
              drilldown, alongside the pre-existing SFDC out-link. Internal
              navigation (react-router `Link`), matching the "Terra ↗" label
              convention used in the drilldown header itself
              (AccountDrillDown.tsx). */}
          <span className="shrink-0 flex items-center gap-2 text-[12px]">
            <Link
              to={accountInCommandCenter(account.id)}
              className="text-content-action dark:text-blueberry-400 hover:underline"
            >
              Terra ↗
            </Link>
            <a
              href={sfdcRecordUrl('Account', account.id)}
              target="_blank" rel="noreferrer"
              className="text-content-action dark:text-blueberry-400 hover:underline"
            >
              SFDC ↗
            </a>
          </span>
        </div>
        {/* Single column — this panel is ~a quarter of the row, not the wide quad. */}
        <AccountFieldsCard account={account} twoColumn={false} />
      </div>
    );
  }

  // account null/404 → thin fallback from the contact row.
  return (
    <div>
      <div className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100 mb-1.5">{row.account_name ?? '—'}</div>
      <DetailKV k="Owner" v={row.owner_name} />
      <DetailKV k="ClayDR" v={row.claydr_name} />
      <DetailKV k="GS" v={row.gs_name} />
      {/* Terra ↗ (2026-08-28, Spencer) — same addition as the rich card above.
          `DetailKV`'s `href` prop only takes a plain string (external-link
          shape), so a `Link`-bearing ReactNode is passed as the value
          instead, same pattern as WentToBlock's internal links below. */}
      {row.account_id && (
        <DetailKV
          k="Terra"
          v={(
            <Link
              to={accountInCommandCenter(row.account_id)}
              className="text-content-action dark:text-blueberry-400 hover:underline"
            >
              Terra ↗
            </Link>
          )}
        />
      )}
      <DetailKV
        k="SFDC"
        v={row.account_id ? 'Account ↗' : null}
        href={row.account_id ? sfdcRecordUrl('Account', row.account_id) : undefined}
      />
      {row.lim_date && (
        <>
          <div className="text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium mt-3 mb-1">Last Interesting Moment</div>
          <DetailKV k="Date" v={fmt(row.lim_date)} />
          <DetailKV k="Type" v={joinNonBlank([row.lim_type, row.lim_subtype])} />
          <DetailKV k="Name" v={row.lim_name} href={row.lim_link ?? undefined} />
          {row.lim_description && (
            <p className="mt-1 text-[11.5px] text-content-secondary dark:text-nightshade-400 leading-relaxed">{row.lim_description}</p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * "Went To" block (U7, Key Technical Decision 8; prototype:1100-1103) — the
 * Alumni-context reverse view: for a contact who has left the account
 * (`no_longer_at_company`), the successor contact's new employer/title at the
 * account they moved TO, plus a link to the successor record itself.
 *
 * NO FETCH (2026-08-27, Spencer): this used to call
 * `GET /api/flow/accounts/:id/went-to` via `useAccountWentTo` and match its
 * own contact out of the account-wide array by `previousContactId`. That
 * endpoint is DELETED — the reverse join now lives in the contacts list query
 * itself (CONTACT_WENT_TO_JOIN_SQL, src/api/flow.ts), so every ContactRow
 * already carries these fields and this block just reads them. Same data,
 * same rules (including the dedup when Salesforce has two successor rows for
 * one alumnus — now the lateral's ORDER BY … LIMIT 1), one less round trip,
 * and the table shows the same three values as real columns.
 *
 * "Alumni context" is still read off the ROW (`no_longer_at_company`), never
 * off which preset is active — ContactDetail receives only the row, and a
 * departed contact IS an alumnus regardless of the view it was opened from.
 * The check is now belt-and-braces (the SQL join carries the same gate).
 *
 * Renders nothing when: not an alumnus, or no successor row matched — the
 * same "no hollow bar" rule as TerraNotesStrip/RecentActionsSection. There is
 * no longer a loading or error state to handle: the fields arrive with the
 * row or not at all.
 *
 * New Contact link (2026-08-28, Spencer — revises the 2026-08-26 Codex
 * adversarial-review decision below): lands on the plain account drilldown
 * (`accountInCommandCenter`) rather than the account drilldown with
 * `?contact=` auto-expand, matching the same-day change to the table's own
 * `new_contact` cell (ContactFlowTable.tsx). Opening the successor's account
 * context is the useful action; forcing an auto-expand into that account's
 * contact roster added friction without benefit. Prior reasoning this
 * replaces: preferred the account-scoped `contactInCommandCenter` over the
 * global `contactsInCommandCenter` search since the successor is generally
 * not an alumnus, so from the Alumni preset it's very likely NOT a row in
 * the global Contacts view's currently loaded/filtered set — that global
 * link's jump-handler toasts "Contact isn't in the current view" instead of
 * navigating when the row isn't found. The global-search fallback (used when
 * no employer account id is available) and the underlying `?contact=`
 * query-param machinery itself are UNCHANGED — still used elsewhere (e.g.
 * the omnibox deep-link, `mini-apps/command-center/index.tsx`).
 */
function WentToBlock({ row }: { row: ContactRow }) {
  if (row.no_longer_at_company !== true) return null;
  if (!row.new_contact_id && !row.new_employer && !row.new_title) return null;

  return (
    <>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium mt-3 mb-1">
        <span>Went To</span>
        <SfdcCloudIcon kind="Contact" id={row.new_contact_id} />
      </div>
      {/* Internal Terra navigation, not sfdcRecordUrl — same links the table's
          New Employer / New Contact cells build (ContactFlowTable.tsx). */}
      <DetailKV
        k="New Employer"
        v={row.new_employer_account_id && row.new_employer ? (
          <Link
            to={accountInCommandCenter(row.new_employer_account_id)}
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            {row.new_employer}
          </Link>
        ) : row.new_employer}
      />
      <DetailKV k="New Title" v={row.new_title} />
      <DetailKV
        k="New Contact"
        v={row.new_contact_id && row.new_contact ? (
          <Link
            to={row.new_employer_account_id
              ? accountInCommandCenter(row.new_employer_account_id)
              : contactsInCommandCenter(row.new_contact_id)}
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            {row.new_contact}
          </Link>
        ) : row.new_contact}
      />
    </>
  );
}

/**
 * "Terra Actions" strip (PR 2, 2026-07-23 revision 3; renamed to "Terra
 * Actions" 2026-08-13, Spencer — user-visible text only, same rule as
 * ContactFlowTable.tsx's CROSS_APP_STATE_LABEL) — the
 * GET /api/flow/contacts/:id/state detail read, rendered as a full-width bar
 * ABOVE the detail panel grid (NOT a grid panel — see ContactDetail below).
 * Small header label + one compact StateFactCard per StateFact, inline,
 * flex-wrap when there are multiples. Reached only by expanding the row
 * (pill clicks never navigate directly — StatePills.tsx has no onClick of
 * its own).
 *
 * Empty/loading/error all render nothing — no empty bar (2026-07-23
 * revision 3 decision): a bar that's sometimes there and sometimes not
 * (depending on data) is a worse signal than never showing a hollow one.
 *
 * Q3 (QA round 3): `recent_request` facts are EXCLUDED from this strip —
 * they render in their own "Recent Actions" section below instead (see
 * `RecentActionsSection`). Mixed in with durable cross-app signals (Gong
 * flow, invites, tasks), a one-off instant-action fire read as confusing —
 * it looked like a persistent cross-app state, not a recent action. This is
 * a frontend-only filter over the SAME useContactStateDetail read; the
 * read-model/backend dimension list is untouched. `ContactFlowTable`'s row
 * strip is a different surface with no sections and keeps every dimension.
 */
function TerraNotesStrip({ contactId, surface }: { contactId: string; surface: Surface }) {
  const { data, isLoading, error } = useContactStateDetail(contactId);

  // validateResponse never throws — on a schema mismatch it warns and hands
  // back the raw, unvalidated payload (see lib/validateResponse.ts), which
  // is NOT guaranteed to carry a `facts` array. This guard is load-bearing,
  // not decorative (mirrors the same guard in ContactFlowTable.tsx for the
  // batch pill endpoint).
  if (isLoading || error || !data || !Array.isArray(data.facts)) {
    return null;
  }

  const stripFacts = data.facts.filter(fact => fact.dimension !== 'recent_request');
  if (stripFacts.length === 0) return null;

  return (
    <div className="mb-2.5 flex items-center gap-1.5 flex-wrap px-2.5 py-1.5 rounded-md border border-secondary dark:border-nightshade-700 bg-bg-secondary/60 dark:bg-nightshade-800/60">
      <span className="text-[9.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium shrink-0">
        Terra Actions
      </span>
      {stripFacts.map(fact => (
        // Fully data-dependent key (no array index): dimension + evidence_ref
        // + observed_at is unique per fact — two adapters never share an
        // evidence_ref, and a single adapter never emits two facts for the
        // same evidence_ref with the same observed_at.
        <StateFactCard key={`${fact.dimension}-${fact.evidence_ref}-${fact.observed_at}`} fact={fact} surface={surface} />
      ))}
    </div>
  );
}

/**
 * "Recent Actions" section (Q3, QA round 3) — the `recent_request` facts
 * excluded from the Terra Actions strip above, rendered as their own labeled
 * group directly below it. Same `useContactStateDetail` read (react-query
 * dedupes the two hook calls onto one network request/cache entry — no
 * duplicate fetch), same `StateFactCard` rendering — purely a frontend split
 * of one strip into two, not a new read-model dimension or endpoint.
 * Renders nothing when there are no recent_request facts (mirrors the Terra
 * Notes strip's own "no hollow bar" rule).
 *
 * flow-workflow-requests ("timestamps + View all", Spencer's spec): shows at
 * MOST `RECENT_REQUEST_RENDER_LIMIT` (2) pills — each pill's own timestamp
 * comes from StateFactCard itself (fact.observed_at, no change needed here).
 * When the backend resolved MORE than that (it fetches up to
 * `RECENT_REQUEST_FETCH_LIMIT` = 3, see contact-state-sources.ts), a
 * section-level "View all →" renders after the pills instead of a 3rd pill —
 * it opens the docked sidebar on the Requests tab, search seeded to this
 * contact, so the rep sees every run.
 */
function RecentActionsSection({ contactId, contactName, surface }: { contactId: string; contactName: string; surface: Surface }) {
  const { data, isLoading, error } = useContactStateDetail(contactId);

  if (isLoading || error || !data || !Array.isArray(data.facts)) {
    return null;
  }

  const recentActionFacts = data.facts.filter(fact => fact.dimension === 'recent_request');
  if (recentActionFacts.length === 0) return null;

  const visibleFacts = recentActionFacts.slice(0, RECENT_REQUEST_RENDER_LIMIT);
  const hasMore = recentActionFacts.length > RECENT_REQUEST_RENDER_LIMIT;

  return (
    <div className="mb-2.5 flex items-center gap-1.5 flex-wrap px-2.5 py-1.5 rounded-md border border-secondary dark:border-nightshade-700 bg-bg-secondary/60 dark:bg-nightshade-800/60">
      <span className="text-[9.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium shrink-0">
        Recent Actions
      </span>
      {visibleFacts.map(fact => (
        <StateFactCard key={`${fact.dimension}-${fact.evidence_ref}-${fact.observed_at}`} fact={fact} surface={surface} />
      ))}
      {hasMore && <ViewAllRequestsButton contactId={contactId} contactName={contactName} />}
    </div>
  );
}

export function ContactDetail({
  row,
  surface = 'command_center',
  leadPanel,
}: {
  row: ContactRow;
  surface?: Surface;
  /**
   * The panel grid's FIRST cell (qa-batch4-plan.md D-A, reversing QA batch 2
   * unit B8's full-width-above-the-grid placement), passed straight through
   * to `FlowDetail` — the Signals tab's Signal card (`SignalDetail.tsx`),
   * left of Contact Info. Every other mount omits it and renders unchanged.
   */
  leadPanel?: FlowDetailPanel;
}) {
  // Clay Role chips read the ROW (2026-09-25): `usage_roles` rides the
  // /api/flow/contacts row this panel already has, folded server-side from
  // the same three SQL booleans the "Clay Role" filters use, so the chips are
  // on screen with the rest of the header instead of after the contact-state
  // detail fetch settles. `ContactHeader.usageRoles` stays on that detail
  // wire for non-row consumers; nothing here reads it any more. Re-parsed
  // because validateResponse never throws and may hand back an unvalidated
  // payload on a schema mismatch.
  const usageRoles = coerceUsageRoles(row.usage_roles);
  // Panel order (2026-09-23, Spencer): Contact Info, Contact Intelligence,
  // Contact Timeline, Account Info, Account Intelligence.
  const contactIntelPanel: FlowDetailPanel = {
    key: 'contact-intel',
    label: 'Contact Intelligence',
    content: <ContactIntelPanel row={row} />,
    headerAction: <PromptSettingsGear ariaLabel="Edit Contact Intelligence prompt" />,
  };
  // D10 — the Account Intelligence / Account Info panels are NEVER
  // suppressed anymore, in any of the 3 mount points (command-center,
  // AccountDrillDown, account-view/ContactTab); `hideAccountPanel` is retired.
  const accountIntelPanel: FlowDetailPanel = {
    key: 'intel',
    label: 'Account Intelligence',
    content: <AccountIntelPanel accountId={row.account_id} />,
  };
  const panels: FlowDetailPanel[] = [
    {
      key: 'contact',
      label: 'Contact Info',
      content: (
            <div>
              {row.no_longer_at_company && (
                <div className="mb-2 px-2 py-1 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-[11px] font-semibold">
                  No longer at company
                </div>
              )}
              <WentToBlock row={row} />
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100 min-w-0 truncate">{row.name ?? '—'}</span>
                    {/* SFDC + LinkedIn out-link "clouds" (action-view parity). */}
                    <SfdcCloudIcon kind="Contact" id={row.id} />
                    <LinkedInIcon url={row.linkedin} />
                  </div>
                  {/*
                    Product Usage role chips (full labels; the table column
                    uses compact mode) on their own line so the name keeps
                    the row: inside the name's flex row, three chips took the
                    width and truncated the name to "Test1 ..." (QA,
                    2026-09-25). Guarded here so RoleChips' own empty dash
                    never appears in the header. The evidence block (won opp,
                    share, tables built, last active) is a deferred follow-up,
                    not part of this header.
                  */}
                  {usageRoles.length > 0 && (
                    <div className="mt-1">
                      <RoleChips roles={usageRoles} />
                    </div>
                  )}
                  <div className="text-[11.5px] text-content-tertiary dark:text-nightshade-500">{row.title ?? '—'}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
                  {/*
                    QA round 2 (R1): the standalone "+ Add to Queue"
                    (the retired EnrollButton) was deliberately NOT rendered
                    here — Spencer's ruling: "Queue for Play" (below) already
                    enrolls the contact into a play in one action, making a
                    separate unassigned-add button redundant right next to it.
                    flow-contacts-bulk-send U7 (decision 12) deleted
                    EnrollButton outright and swapped ContactFlowTable's row
                    action to this same WorkflowsDropdown/ContactActionMenus
                    component — there is no separate button anywhere anymore.
                  */}
                  <ContactActionMenus
                    contactId={row.id}
                    contactName={row.name}
                    contactEmail={row.email}
                    accountId={row.account_id}
                  />
                </div>
              </div>
              <DetailKV k="Email" v={row.email} href={row.email ? `mailto:${row.email}` : undefined} />
              <DetailKV k="Best Phone" v={row.best_phone} />
              <DetailKV k="Persona" v={row.persona} />
              <DetailKV k="Persona Tier" v={row.persona_tier} />
              <DetailKV k="Stage" v={row.contact_stage} />
              <DetailKV k="Job Level" v={row.job_level} />
              <DetailKV k="Location" v={[row.mailing_state, row.mailing_country].filter(Boolean).join(', ') || null} />
              <DetailKV k="Fit" v={row.fit_score} />
              <DetailKV k="Total Eng." v={row.total_engagement_score} />
              <DetailKV k="Product Eng." v={row.product_engagement_score} />
              <DetailKV k="Signal Eng." v={row.signals_engagement_score} />
              <DetailKV k="Last WS Login" v={fmt(row.last_workspace_login)} />
              <DetailKV k="Last Email Sent" v={fmt(row.last_email_sent)} />
              {row.gong_in_flow && (
                <DetailKV k="Gong Flow" v={`⚡ ${row.gong_flow_name ?? 'In flow'}`} />
              )}
        </div>
      ),
    },
    contactIntelPanel,
    {
      key: 'timeline',
      label: 'Contact Timeline',
      // U8: threads the contact's own account id so ContactTimeline can
      // compare it against each workspace entry's workspaceAccountId for the
      // Main/Separate badge — the shorter thread of the two options in the
      // plan's resolved question (ContactDetailPanels already has
      // row.account_id in scope; see the badge-comparison caveat on
      // FlowContactWorkspaceUserSchema.workspaceAccountId).
      // `surface` (bead .16) threads through so ContactTimeline can render
      // the Signals row-action buttons on its own `action` entries ONLY
      // when this card is mounted from the Signals tab (`SignalDetail.tsx`'s
      // `surface="signals"` below) — every other mount (Command Center,
      // Playhouse, …) keeps its default 'command_center' and renders
      // byte-for-byte as before.
      content: <ContactTimeline contactId={row.id} accountId={row.account_id} contactEmail={row.email} surface={surface} />,
    },
  ];

  panels.push({
    key: 'account',
    label: 'Account Info',
    content: <AccountPanel row={row} />,
  });
  panels.push(accountIntelPanel);

  // Terra Actions renders as a full-width strip ABOVE the panel grid (2026-07-23
  // revision 3) — NOT one of the grid panels above. Same data hook
  // (useContactStateDetail) as before; only the layout changed. Recent
  // Actions (Q3, QA round 3) is a second, separately-labeled strip directly
  // below it, over the same read — see TerraNotesStrip/RecentActionsSection.
  return (
    <FlowDetail
      panels={panels}
      leadPanel={leadPanel}
      topStrip={
        <>
          <TerraNotesStrip contactId={row.id} surface={surface} />
          <RecentActionsSection contactId={row.id} contactName={row.name ?? row.email ?? ''} surface={surface} />
        </>
      }
    />
  );
}
